/**
 * Minimal Shopify Storefront API client — browser-only.
 *
 * - POST to `https://{shop}/api/{version}/graphql.json`.
 * - Automatic `@inContext(country, language)` variable injection when the
 *   query declares `$country` or `$language` parameters.
 * - Tiny in-memory cache keyed on a hash of (query + variables). TTL is
 *   `config.cacheDuration`. Disable per call via `{ cache: false }`.
 * - Throws if `data === null` or `errors` is present.
 */

import { config } from "../config.js";
import { getCurrentCountry, getCurrentLocale } from "../i18n/locale.js";
import type {
  StorefrontGraphQLError,
  StorefrontResponse,
} from "../types.js";

// ---------------------------------------------------------------------------
//  Error class
// ---------------------------------------------------------------------------

export class StorefrontError extends Error {
  readonly graphQLErrors: StorefrontGraphQLError[];
  readonly status: number;

  constructor(
    message: string,
    opts: { graphQLErrors?: StorefrontGraphQLError[]; status?: number } = {},
  ) {
    super(message);
    this.name = "StorefrontError";
    this.graphQLErrors = opts.graphQLErrors ?? [];
    this.status = opts.status ?? 0;
  }
}

// ---------------------------------------------------------------------------
//  Cache
// ---------------------------------------------------------------------------

interface CacheEntry {
  value: unknown;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry>();

function hashKey(query: string, variables: Record<string, unknown>): string {
  // Fast-enough, non-crypto hash. The point is uniqueness within a tab, not
  // secrecy. We prefix with query length to avoid trivial collisions.
  const raw = `${query.length}:${query}\u241F${stableStringify(variables)}`;
  let h = 2166136261;
  for (let i = 0; i < raw.length; i++) {
    h ^= raw.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

function stableStringify(v: unknown): string {
  if (v === null || typeof v !== "object") return JSON.stringify(v);
  if (Array.isArray(v)) {
    return `[${v.map((x) => stableStringify(x)).join(",")}]`;
  }
  const rec = v as Record<string, unknown>;
  const keys = Object.keys(rec).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify(rec[k])}`)
    .join(",")}}`;
}

// ---------------------------------------------------------------------------
//  Context injection
// ---------------------------------------------------------------------------

const COUNTRY_VAR_RE = /\$country\b/;
const LANGUAGE_VAR_RE = /\$language\b/;

function injectContext(
  query: string,
  variables: Record<string, unknown>,
): Record<string, unknown> {
  const wantsCountry = COUNTRY_VAR_RE.test(query);
  const wantsLanguage = LANGUAGE_VAR_RE.test(query);
  if (!wantsCountry && !wantsLanguage) return variables;

  const next: Record<string, unknown> = { ...variables };
  if (wantsCountry && next["country"] === undefined) {
    next["country"] = getCurrentCountry().toUpperCase();
  }
  if (wantsLanguage && next["language"] === undefined) {
    next["language"] = getCurrentLocale().toUpperCase();
  }
  return next;
}

// ---------------------------------------------------------------------------
//  Concurrency limiter (semaphore)
// ---------------------------------------------------------------------------
//
//  La page filter peut déclencher 1500+ Storefront queries en parallèle (1 par
//  card). Sans throttle, ça :
//   - sature le navigateur (~6 connexions HTTP/1.1 max → file d'attente browser)
//   - sature l'API Shopify (rate limit cost-based)
//   - cause un freeze visuel : aucune card hydratée pendant 5-10s puis tout
//     d'un coup (mauvaise UX perçue comme bug "rien ne s'affiche")
//
//  Avec un sémaphore N=8 : les 8 premières queries partent immédiatement, les
//  suivantes attendent qu'une slot se libère. Les 40 premières cards (page 1)
//  sont hydratées en ~1s, le reste en background.

const MAX_CONCURRENT = 20;
let inFlight = 0;
const waiters: Array<() => void> = [];

async function acquireSlot(): Promise<void> {
  if (inFlight < MAX_CONCURRENT) {
    inFlight++;
    return;
  }
  await new Promise<void>((resolve) => waiters.push(resolve));
  inFlight++;
}

function releaseSlot(): void {
  inFlight--;
  const next = waiters.shift();
  if (next) next();
}

// ---------------------------------------------------------------------------
//  Public API
// ---------------------------------------------------------------------------

export interface QueryOptions {
  /** Disable cache for this call (default: cache is on if TTL > 0). */
  cache?: boolean;
  /** Override TTL for this call, in ms. */
  cacheTtl?: number;
}

function endpoint(): string {
  return `https://${config.shopDomain}/api/${config.storefrontApiVersion}/graphql.json`;
}

async function rawFetch<T>(
  query: string,
  variables: Record<string, unknown>,
): Promise<T> {
  await acquireSlot();
  let res: Response;
  try {
    res = await fetch(endpoint(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Shopify-Storefront-Access-Token": config.storefrontToken,
      },
      body: JSON.stringify({ query, variables }),
    });
  } finally {
    releaseSlot();
  }

  let json: StorefrontResponse<T> | null = null;
  try {
    json = (await res.json()) as StorefrontResponse<T>;
  } catch {
    throw new StorefrontError(
      `Storefront API returned non-JSON (status ${res.status})`,
      { status: res.status },
    );
  }

  if (!res.ok) {
    throw new StorefrontError(`Storefront API HTTP ${res.status}`, {
      status: res.status,
      graphQLErrors: json?.errors ?? [],
    });
  }

  if (json.errors && json.errors.length > 0) {
    const msg = json.errors.map((e) => e.message).join("; ");
    throw new StorefrontError(`Storefront GraphQL errors: ${msg}`, {
      graphQLErrors: json.errors,
      status: res.status,
    });
  }

  if (json.data === null || json.data === undefined) {
    throw new StorefrontError("Storefront GraphQL returned null data", {
      status: res.status,
    });
  }

  return json.data;
}

/**
 * Flatten GraphQL Connections récursivement :
 *   {nodes: [...]}            → [...]
 *   {edges: [{node: ...}, ...]} → [...]
 * Mutation profonde (in-place) pour éviter d'allouer un nouvel objet complet.
 * Non destructive : si la shape n'a ni `nodes` ni `edges`, on descend récursivement.
 */
function flattenConnections(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) value[i] = flattenConnections(value[i]);
    return value;
  }
  if (typeof value !== "object") return value;
  const obj = value as Record<string, unknown>;
  // Detect Connection shapes.
  if (Array.isArray(obj.nodes) && Object.keys(obj).length <= 3) {
    const nodes = obj.nodes as unknown[];
    for (let i = 0; i < nodes.length; i++) nodes[i] = flattenConnections(nodes[i]);
    return nodes;
  }
  if (Array.isArray(obj.edges) && Object.keys(obj).length <= 3) {
    const flat: unknown[] = [];
    for (const edge of obj.edges) {
      if (edge && typeof edge === "object" && "node" in edge) {
        flat.push(flattenConnections((edge as { node: unknown }).node));
      }
    }
    return flat;
  }
  // Descend récursivement sur les autres objets.
  for (const k of Object.keys(obj)) {
    obj[k] = flattenConnections(obj[k]);
  }
  return obj;
}

async function query<T>(
  graphql: string,
  variables: Record<string, unknown> = {},
  options: QueryOptions = {},
): Promise<T> {
  const enriched = injectContext(graphql, variables);
  const ttl = options.cacheTtl ?? config.cacheDuration;
  const useCache = options.cache !== false && ttl > 0;

  const key = useCache ? hashKey(graphql, enriched) : "";

  if (useCache) {
    const hit = cache.get(key);
    if (hit && hit.expiresAt > Date.now()) {
      return hit.value as T;
    }
    if (hit) cache.delete(key);
  }

  const raw = await rawFetch<T>(graphql, enriched);
  const data = flattenConnections(raw) as T;

  if (useCache) {
    cache.set(key, { value: data, expiresAt: Date.now() + ttl });
  }

  return data;
}

function invalidateCache(): void {
  cache.clear();
}

export const storefront = {
  query,
  invalidateCache,
};
